import { useEffect } from "react";
import { cn } from "@/lib/utils";
import TrackStreamDetector from "../TrackStreamDetector";
import { Track } from "@/types";
import { useIsMobile, useIsTablet } from "@/hooks/useResponsive";
import { usePlayerAutoCollapse } from "./usePlayerAutoCollapse";
import { usePlayerAutoHide } from "./usePlayerAutoHide";
import { useSignedAudioUrl } from "@/hooks/useSignedAudioUrl";
import { useMusic } from "@/contexts/MusicContext";
import { useHideOnScroll } from "@/hooks/useHideOnScroll";

// Console warning for DevTools users
const showProtectionWarning = () => {
  console.log(
    "%c⚠️ Audio Protection Active",
    "color: red; font-size: 20px; font-weight: bold;",
  );
  console.log(
    "%cDownloading or distributing this content without permission is prohibited.",
    "color: orange; font-size: 14px;",
  );
};

interface PlayerContainerProps {
  isExpanded: boolean;
  mobileBottomOffsetClass?: string;
  // Lifts the collapsed player clear of the floating tablet dock
  // (PublicMobileBottomBar) on sidebar-shell pages. Left unset, tablet keeps
  // its previous bottom-0 desktop-style docking (pages with no tablet nav).
  tabletBottomOffsetClass?: string;
  // On desktop sidebar-shell pages, keeps the fixed player clear of
  // PublicSidebar's own width so it doesn't sit on top of the rail's account
  // menu in the bottom-left corner. Left unset, desktop keeps spanning the
  // full viewport width (pages with no persistent left rail).
  desktopSidebarOffsetClass?: string;
  currentTrack: Track;
  isPlaying: boolean;
  isActuallyPlaying: boolean;
  setIsActuallyPlaying: (playing: boolean) => void;
  audioRef: React.RefObject<HTMLAudioElement>;
  onStreamComplete: () => void;
  onTimeUpdate: () => void;
  setIsPlaying: (playing: boolean) => void;
  onCollapse?: () => void;
  children: React.ReactNode;
}

export function PlayerContainer({
  isExpanded,
  mobileBottomOffsetClass,
  tabletBottomOffsetClass,
  desktopSidebarOffsetClass,
  currentTrack,
  isPlaying,
  isActuallyPlaying,
  setIsActuallyPlaying,
  audioRef,
  onStreamComplete,
  onTimeUpdate,
  setIsPlaying,
  onCollapse,
  children,
}: PlayerContainerProps) {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const { isTransitioning, setIsTransitioning, isImmersiveFeedActive } = useMusic();
  const navHidden = useHideOnScroll();

  const { playerRef } = usePlayerAutoCollapse({
    isExpanded,
    onCollapse: onCollapse || (() => {}),
  });

  const { isHidden, reveal } = usePlayerAutoHide({
    isActuallyPlaying,
    isExpanded,
  });

  // Use signed URL with error handling - supports preview mode for logged out users
  const {
    audioUrl,
    isLoading: isUrlLoading,
    error: signedUrlError,
    isPreview,
  } = useSignedAudioUrl(
    currentTrack?.id || null,
    currentTrack?.audioUrl || null,
  );

  // Log preview mode for debugging
  useEffect(() => {
    if (isPreview && currentTrack) {
      console.log("🎵 Preview mode active for track:", currentTrack.title);
    }
  }, [isPreview, currentTrack]);

  // Determine the effective audio URL - only use signed URL, never fall back to public URL
  // since the tracks bucket is private and public URLs won't work
  const effectiveAudioUrl = audioUrl || null;

  // Update audio source when signed URL changes
  useEffect(() => {
    if (audioRef.current && effectiveAudioUrl && !isUrlLoading) {
      const currentSrc = audioRef.current.src;
      // Only update if URL actually changed (avoid unnecessary reloads)
      if (currentSrc !== effectiveAudioUrl) {
        const wasPlaying = !audioRef.current.paused;
        const currentTime = audioRef.current.currentTime;

        audioRef.current.src = effectiveAudioUrl;

        // Restore playback position if it was playing
        if (wasPlaying && currentTime > 0) {
          audioRef.current.currentTime = currentTime;
          audioRef.current.play().catch(console.error);
        }
      }
    }
  }, [effectiveAudioUrl, isUrlLoading, audioRef]);

  // Handle track transitions - start playback when new URL is ready
  useEffect(() => {
    if (
      isTransitioning &&
      effectiveAudioUrl &&
      !isUrlLoading &&
      audioRef.current
    ) {
      const audioSrc = audioRef.current.src;
      // URL is ready for the new track - start playback
      if (audioSrc === effectiveAudioUrl) {
        console.log(
          "🎵 New track URL ready, starting playback for:",
          currentTrack?.title,
        );
        setIsTransitioning(false);
        setIsPlaying(true);
        audioRef.current.play().catch((error) => {
          if (error?.name === "AbortError") {
            console.log("⏭️ Transition playback aborted (expected)");
            return;
          }
          console.error("Playback error during transition:", error);
          setIsPlaying(false);
        });
      }
    }
  }, [
    isTransitioning,
    effectiveAudioUrl,
    isUrlLoading,
    audioRef,
    setIsPlaying,
    setIsTransitioning,
    currentTrack,
  ]);

  // Trigger playback when URL becomes ready while isPlaying is true (for manual play clicks)
  useEffect(() => {
    // Skip if we're in transition mode - that effect handles playback
    if (isTransitioning) return;

    if (isPlaying && effectiveAudioUrl && !isUrlLoading && audioRef.current) {
      const audioSrc = audioRef.current.src;
      // If the audio element has the correct source but isn't playing, start playback
      if (audioSrc === effectiveAudioUrl && audioRef.current.paused) {
        console.log("🎵 URL ready, triggering playback");
        audioRef.current.play().catch((error) => {
          // AbortError is expected when switching tracks - don't show error
          if (error?.name === "AbortError") {
            console.log("⏭️ URL-ready playback aborted (track changed)");
            return;
          }
          console.error("Playback error after URL ready:", error);
          setIsPlaying(false);
        });
      }
    }
  }, [
    isPlaying,
    effectiveAudioUrl,
    isUrlLoading,
    audioRef,
    setIsPlaying,
    isTransitioning,
  ]);

  // Clear audio source immediately when transitioning to prevent stale playback
  useEffect(() => {
    if (isTransitioning && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeAttribute("src");
      audioRef.current.load();
    }
  }, [isTransitioning, audioRef]);

  // Show protection warning in console
  useEffect(() => {
    showProtectionWarning();
  }, []);

  return (
    <div
      ref={playerRef}
      onContextMenu={(e) => e.preventDefault()}
      onClickCapture={
        isHidden
          ? (e) => {
              e.preventDefault();
              e.stopPropagation();
              reveal();
            }
          : undefined
      }
      className={cn(
        "fixed left-0 right-0 z-[65] audio-protected",
        // The compact artist/track feed (49a/50a) is a full-bleed,
        // chrome-free TikTok-style view with no docked player in its own
        // design — hide the bar while it's mounted rather than overlay it.
        // Playback/stream-tracking continue regardless; only visibility here.
        isImmersiveFeedActive && "hidden",
        desktopSidebarOffsetClass,
        "bg-[#0E0C24]/95 backdrop-blur-lg border-t border-white/[0.08]",
        "transition-[bottom,height,top,transform] duration-300 ease-in-out motion-reduce:transition-none",
        // Paused and left untouched: slide down to a thin, still-tappable
        // sliver instead of permanently holding its full height. Tapping it
        // (anywhere) reveals the bar again rather than acting on what's
        // underneath the tap — see the onClickCapture guard above.
        isHidden && "translate-y-[calc(100%-10px)]",
        // Mobile-optimized heights and spacing
        isExpanded
          ? isMobile
            ? // Sized to its own content (track art, controls, actions)
              // instead of always filling the screen below the navbar —
              // most tracks' "Now Playing" content is much shorter than a
              // full viewport, so forcing full height just left a big
              // empty gap below it. Still bottom-anchored like the
              // collapsed bar, with a cap so it can never grow past the
              // navbar on a very tall content case.
              "h-auto max-h-[calc(100vh-60px)] overflow-y-auto bottom-0 rounded-t-[14px]"
            : "h-[500px] bottom-0"
          : isMobile
            ? cn(
                "h-16",
                // Pages with their own floating/full-width mobile nav
                // (mobileBottomOffsetClass set) keep the player pinned
                // above it at all times — only the classic bottom nav
                // (which actually slides away on scroll) lets the player
                // drop to the very bottom to fill the gap it leaves.
                mobileBottomOffsetClass
                  ? mobileBottomOffsetClass
                  : navHidden
                    ? "bottom-0"
                    : "bottom-[64px]",
              )
            : isTablet && tabletBottomOffsetClass
              ? cn("h-24", tabletBottomOffsetClass, "mx-2 rounded-t-lg")
              : "h-24 bottom-0 mx-2 rounded-t-lg",
        "md:mx-0 md:rounded-none", // Remove margins on desktop
      )}
    >
      {currentTrack && (
        <TrackStreamDetector
          track={currentTrack}
          isPlaying={isActuallyPlaying}
          onStreamComplete={onStreamComplete}
        />
      )}

      <audio
        ref={audioRef}
        data-player-audio="true"
        src={effectiveAudioUrl || ""}
        onTimeUpdate={onTimeUpdate}
        // "playing" (not "play") is what confirms audio is actually audible —
        // "play" fires the instant playback is requested, even while the
        // browser is still buffering, which previously let isActuallyPlaying
        // go true (and the 30s stream-payment timer start) for a track that
        // was stuck loading and never produced any sound.
        onPlaying={() => setIsActuallyPlaying(true)}
        onPause={() => setIsActuallyPlaying(false)}
        // Mid-stream buffering/stalls stop real playback without firing
        // pause/ended/error — pause the stream timer here too, so a stall
        // doesn't keep silently accumulating "listened" time. onPlaying
        // fires again once playback actually resumes.
        onWaiting={() => setIsActuallyPlaying(false)}
        onStalled={() => setIsActuallyPlaying(false)}
        onEnded={() => {
          if (isTransitioning) return;
          setIsActuallyPlaying(false);
          setIsPlaying(false);
          onStreamComplete();
        }}
        onError={(e) => {
          setIsActuallyPlaying(false);
          // Only log if there's actually a source set
          if (effectiveAudioUrl) {
            console.error("Audio element error:", e);
          }
        }}
        onContextMenu={(e) => e.preventDefault()}
        className="hidden"
      />

      <div className="h-full flex flex-col relative">{children}</div>
    </div>
  );
}
